import { Link } from "react-router-dom";
import { ArrowUpRight, Clock, Tag } from "lucide-react";
import SEO from "@/components/SEO";
import { sized, srcSet } from "@/lib/images";
import CtaBanner from "@/components/CtaBanner";
import { BLOG_POSTS } from "@/data/blog";

export default function Blog() {
  return (
    <>
      <SEO
        title="Blog — E-commerce Marketplace Insights for Indian Brands"
        description="Practical guides on Amazon, Flipkart, Meesho, JioMart and quick commerce. Onboarding playbooks, scaling tactics and account health insights."
        path="/blog"
      />

      <section className="border-b-2 border-black bg-white py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="tape">Insights</div>
          <h1 className="display mt-6 max-w-4xl text-5xl font-black leading-[0.95] tracking-tight md:text-6xl lg:text-7xl">
            The marketplace<br />
            <span className="bg-[#FFD700] px-2">playbook.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base text-black/70 md:text-lg">
            Hands-on guides, frameworks and tactics — written by the team that runs 200+ marketplace accounts every day.
          </p>
        </div>
      </section>

      <section className="bg-[#FAFAF7] py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            {BLOG_POSTS.map((p, i) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}/`}
                data-testid={`blog-card-${p.slug}`}
                className="card-brutal group flex flex-col overflow-hidden p-0"
              >
                <div className="aspect-[16/10] w-full overflow-hidden border-b-2 border-black">
                  <img
                    src={sized(p.cover, 640)}
                    srcSet={srcSet(p.cover, [480, 640, 960])}
                    sizes="(min-width: 1024px) 380px, (min-width: 768px) 50vw, 100vw"
                    width="640"
                    height="400"
                    alt={p.title}
                    loading={i < 2 ? "eager" : "lazy"}
                    decoding="async"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <div className="flex items-center gap-3 text-[11px] font-bold uppercase tracking-widest text-black/60">
                    <span className="flex items-center gap-1"><Tag size={12} /> {p.category}</span>
                    <span>·</span>
                    <span className="flex items-center gap-1"><Clock size={12} /> {p.readTime}</span>
                  </div>
                  <h3 className="display mt-3 flex-1 text-xl font-black leading-tight tracking-tight md:text-2xl">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-black/70 line-clamp-3">{p.excerpt}</p>
                  <div className="mt-5 flex items-center justify-between border-t-2 border-black pt-4">
                    <span className="text-xs font-bold text-black/60">{p.date}</span>
                    <span className="flex items-center gap-1 text-xs font-bold uppercase tracking-widest">
                      Read <ArrowUpRight size={14} />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CtaBanner
        title="Want a custom growth playbook?"
        subtitle="Talk to our team about your category, channels and goals. Free strategy call."
      />
    </>
  );
}
